import { useQuery } from "@tanstack/react-query";
import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../contexts/AuthProvider";
import Loading from "../../Shared/Loading/Loading";
import { ScaleLoader } from "react-spinners";
import { LazyLoadImage } from "react-lazy-load-image-component";
import addPhoneImg from "../../../assets/images/add-phone/add-phone.png";
import { TypeAnimation } from "react-type-animation";

const AddProduct = () => {
   const {
      register,
      handleSubmit,
      formState: { errors },
   } = useForm();

   const { user } = useContext(AuthContext);
   const imageHostKey = process.env.REACT_APP_imgbb_key;
   const navigate = useNavigate();

   const postedDate = new Date().toLocaleDateString();
   const postedTime = new Date().toLocaleTimeString();

   const {
      data: categories,
      isLoading,
      refetch,
   } = useQuery({
      queryKey: ["category"],
      queryFn: async () => {
         const res = await fetch(
            "https://recycle-hut-server.vercel.app/categories"
         );
         const data = await res.json();
         return data;
      },
   });

   const [loading, setLoading] = useState(false);

   const handleAddProduct = (data) => {
      const image = data.image[0];
      const formData = new FormData();
      formData.append("image", image);

      setLoading(true);
      const url = `https://api.imgbb.com/1/upload?key=${imageHostKey}`;
      fetch(url, {
         method: "POST",
         body: formData,
      })
         .then((res) => res.json())
         .then((imgData) => {
            if (imgData.success) {
               console.log(imgData.data.url);
            }
            const product = {
               postedDate,
               postedTime,
               productImg: imgData.data.url,
               productName: data.name,
               brand: data.brand,
               model: data.model,
               category: data.category,
               description: data.description,
               sellingReason: data.sellingReason,
               yearsOfUse: data.yearsOfUse,
               conditionType: data.conditionType,
               resalePrice: data.resalePrice,
               originalPrice: data.originalPrice,
               sellerName: data.sellerName,
               sellerMobile: data.sellerMobile,
               sellerEmail: user.email,
               location: data.location,
            };
            console.log("product:", product);

            fetch("https://recycle-hut-server.vercel.app/products", {
               method: "POST",
               headers: {
                  "content-type": "application/json",
               },
               body: JSON.stringify(product),
            })
               .then((res) => res.json())
               .then((result) => {
                  console.log(result);
                  toast.success(`${data.name} is added successfully`);
                  navigate("/dashboard/myproducts");
                  refetch();
                  setLoading(false);
               });
         });
   };

   if (isLoading) {
      return <Loading></Loading>;
   }

   return (
      <div className="w-[91%] md:w-full mx-auto mb-7 md:mb-10"
         data-aos="fade-left" data-aos-duration="1500"
      >
         <div className="flex flex-col justify-between items-center">
            <div data-aos="fade-down"
               data-aos-duration="1500">
               <LazyLoadImage
                  src={addPhoneImg}
                  alt="add-phone-img"
                  className="w-full rounded-xl mt-7 md:mt-10 mb-5"
                  placeholderSrc={addPhoneImg}
                  effect="blur"
                  title="Add A Phone"
               />
            </div>

            <div className="w-full md:w-[85%] lg:w-[80%] mx-auto"
               data-aos="fade-up"
               data-aos-duration="1500"
            >
               <h2 className="text-[22px] md:text-[26px] text-center mb-4 lg:mb-5 text-blue-700 font-bold opacity-80 my-transition-4">
                  <TypeAnimation
                     sequence={["Add A Phone", 3000]}
                     speed={50}
                     cursor={true}
                     repeat={Infinity}
                  />
               </h2>
               <form
                  onSubmit={handleSubmit(handleAddProduct)}
                  className="border bg-base-100 rounded-xl p-6 w-full uncommon shadow-xl"
               >
                  <div className="flex flex-col md:flex-row gap-1 md:gap-5">
                     <div className="form-control w-full">
                        <label className="label">
                           <span className="label-text font-semibold">Phone Name</span>
                        </label>

                        <input
                           type="text"
                           {...register("name", {
                              required: "Name is Required",
                           })}
                           className="input input-bordered w-full mb-1"
                        />

                        {errors.name && (
                           <p className="text-red-500 font-semibold">
                              {errors.name.message}
                           </p>
                        )}
                     </div>

                     <div className="form-control w-full">
                        <label className="label">
                           <span className="label-text font-semibold">Phone Image</span>
                        </label>
                        <input
                           type="file"
                           {...register("image", {
                              required: "Photo is Required",
                           })}
                           className="input input-bordered w-full mb-1"
                        />
                        {errors.image && (
                           <p className="text-red-500 font-semibold">
                              {errors.image.message}
                           </p>
                        )}
                     </div>
                  </div>

                  <div className="flex gap-4 md:gap-5">
                     <div className="form-control w-full">
                        <label className="label mt-2">
                           <span className="label-text font-semibold">Phone Brand</span>
                        </label>
                        <input
                           type="text"
                           {...register("brand", {
                              required: "Brand is Required",
                           })}
                           className="input input-bordered w-full mb-1"
                        />
                        {errors.brand && (
                           <p className="text-red-500 font-semibold">
                              {errors.brand.message}
                           </p>
                        )}
                     </div>

                     <div className="form-control w-full">
                        <label className="label mt-2">
                           <span className="label-text font-semibold">Phone Model</span>
                        </label>
                        <input
                           type="text"
                           {...register("model", {
                              required: "Model is Required",
                           })}
                           className="input input-bordered w-full mb-1"
                        />
                        {errors.model && (
                           <p className="text-red-500 font-semibold">
                              {errors.model.message}
                           </p>
                        )}
                     </div>
                  </div>

                  <div className="flex gap-4 md:gap-5">
                     <div className="form-control w-full">
                        <label className="label mt-2">
                           <span className="label-text font-semibold">
                              Phone Category
                           </span>
                        </label>
                        <select
                           {...register("category")}
                           className="select input-bordered w-full font-normal"
                        >
                           {categories.map((category) => (
                              <option key={category._id} value={category.name}>
                                 {category.name}
                              </option>
                           ))}
                        </select>
                     </div>

                     <div className="form-control w-full">
                        <label className="label mt-2">
                           <span className="label-text font-semibold">
                              Condition Type
                           </span>
                        </label>

                        <select
                           {...register("conditionType")}
                           className="select input-bordered w-full font-normal"
                        >
                           <option value="Excellent" defaultValue="Excellent">
                              Excellent
                           </option>
                           <option value="Good">Good</option>
                           <option value="Fair">Fair</option>
                        </select>
                     </div>
                  </div>

                  <div className="flex gap-4 md:gap-5">
                     <div className="form-control w-full">
                        <label className="label mt-2">
                           <span className="label-text font-semibold">Year(s) of Used</span>
                        </label>
                        <input
                           type="text"
                           {...register("yearsOfUse", {
                              required: "Years of Use is Required",
                           })}
                           className="input input-bordered w-full mb-1"
                        />
                        {errors.yearsOfUse && (
                           <p className="text-red-500 font-semibold">
                              {errors.yearsOfUse.message}
                           </p>
                        )}
                     </div>

                     <div className="form-control w-full">
                        <label className="label mt-2">
                           <span className="label-text font-semibold">Buying Price (Tk)</span>
                        </label>
                        <input
                           type="text"
                           {...register("originalPrice", {
                              required: "Original Price is Required",
                           })}
                           className="input input-bordered w-full mb-1"
                        />
                        {errors.originalPrice && (
                           <p className="text-red-500 font-semibold">
                              {errors.originalPrice.message}
                           </p>
                        )}
                     </div>

                     <div className="form-control w-full">
                        <label className="label mt-2">
                           <span className="label-text font-semibold">
                              Selling Price (Tk)
                           </span>
                        </label>
                        <input
                           type="text"
                           {...register("resalePrice", {
                              required: "Resale Price is Required",
                           })}
                           className="input input-bordered w-full mb-1"
                        />
                        {errors.resalePrice && (
                           <p className="text-red-500 font-semibold">
                              {errors.resalePrice.message}
                           </p>
                        )}
                     </div>
                  </div>

                  <div className="flex flex-col md:flex-row gap-1 md:gap-5">
                     <div className="form-control w-full">
                        <label className="label mt-2">
                           <span className="label-text font-semibold">Seller Name</span>
                        </label>
                        <input
                           readOnly
                           type="text"
                           defaultValue={user?.displayName}
                           {...register("sellerName", {
                              required: false,
                           })}
                           className="input outline-none w-full mb-1 bg-base-200"
                        />
                        {errors.sellerName && (
                           <p className="text-red-500 font-semibold">
                              {errors.sellerName.message}
                           </p>
                        )}
                     </div>

                     <div className="form-control w-full">
                        <label className="label mt-2">
                           <span className="label-text font-semibold">Seller Email</span>
                        </label>
                        <input
                           type="email"
                           defaultValue={user?.email}
                           readOnly
                           {...register("sellerEmail", {
                              required: false,
                           })}
                           className="input outline-none w-full mb-1 bg-base-200"
                        />
                        {errors.sellerEmail && (
                           <p className="text-red-500 font-semibold">
                              {errors.sellerEmail.message}
                           </p>
                        )}
                     </div>
                  </div>

                  <div className="flex gap-4 md:gap-5">
                     <div className="form-control w-full">
                        <label className="label mt-2">
                           <span className="label-text font-semibold">
                              Seller Mobile
                           </span>
                        </label>
                        <input
                           type="text"
                           {...register("sellerMobile", {
                              required: "Seller Mobile is Required",
                           })}
                           className="input input-bordered w-full mb-1"
                        />
                        {errors.sellerMobile && (
                           <p className="text-red-500 font-semibold">
                              {errors.sellerMobile.message}
                           </p>
                        )}
                     </div>

                     <div className="form-control w-full">
                        <label className="label mt-2">
                           <span className="label-text font-semibold">
                              Seller Location
                           </span>
                        </label>
                        <input
                           type="text"
                           {...register("location", {
                              required: "Seller Location is Required",
                           })}
                           className="input input-bordered w-full mb-1"
                        />
                        {errors.location && (
                           <p className="text-red-500 font-semibold">
                              {errors.location.message}
                           </p>
                        )}
                     </div>
                  </div>

                  <div className="flex flex-col md:flex-row gap-1 md:gap-5">
                     <div className="form-control w-full">
                        <label className="label mt-2">
                           <span className="label-text font-semibold">
                              Phone Details
                           </span>
                        </label>

                        <textarea
                           className="textarea textarea-bordered mb-1"
                           placeholder=""
                           {...register("description", {
                              required: "Phone Details is Required",
                           })}
                        ></textarea>

                        {errors.description && (
                           <p className="text-red-500 font-semibold">
                              {errors.description.message}
                           </p>
                        )}
                     </div>

                     <div className="form-control w-full">
                        <label className="label mt-2">
                           <span className="label-text font-semibold">
                              Reason To Sell
                           </span>
                        </label>

                        <textarea
                           className="textarea textarea-bordered mb-1"
                           placeholder=""
                           {...register("sellingReason", {
                              required: "Selling Reason is Required",
                           })}
                        ></textarea>

                        {errors.sellingReason && (
                           <p className="text-red-500 font-semibold">
                              {errors.sellingReason.message}
                           </p>
                        )}
                     </div>
                  </div>

                  <button
                     className="btn btn-accent bg-cyan-500 text-white mt-4 w-full font-bold"
                     value="add phone"
                  >
                     {loading ? <ScaleLoader color="#fff" height={16} /> : "Add Phone"}
                  </button>
               </form>
            </div>
         </div>
      </div>
   );
};

export default AddProduct;
